import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import type { User } from "@supabase/supabase-js";
import {
  supabase,
  isSupabaseConnected,
  clearSupabaseSessionCache,
} from "../lib/supabase";

export interface UserProfile {
  id: string;
  name: string | null;
  phone: string | null;
  username: string | null;
  avatar_url: string | null;
  membership_level: string;
  account_status: string;
  created_at: string | null;
  balance: number;
  total_earnings: number;
  is_admin: boolean;
  bank_name: string | null;
  branch_code: string | null;
  bank_account: string | null;
  account_holder: string | null;
  is_verified: boolean;
  verification_status: string | null;
  full_name: string | null;
  withdraw_password: string | null;
  withdraw_error_count: number;
  id_card_front: string | null;
  id_card_back: string | null;
  id_card_holding: string | null;
}

interface AuthContextValue {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  authActionLoading: boolean;
  isLoggedIn: boolean;
  isAdmin: boolean;
  signUp: (
    username: string,
    password: string,
    name: string,
    withdrawPassword: string,
    phone?: string,
  ) => Promise<{ user: User | null; session: unknown }>;
  signIn: (username: string, password: string) => Promise<unknown>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<Omit<UserProfile, "id" | "created_at">>) => Promise<void>;
  getUserIdentifier: () => string | null;
  fetchProfile: (userId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const FAILED_ATTEMPTS_KEY = "auth_failed_attempts";
const MAX_FAILED_ATTEMPTS = 5;

function isAdminRoute(): boolean {
  const path = window.location.pathname;
  // 兼容可能的 basePath 前綴（如 /app/admin）
  const normalized = path.replace(/^\/[^/]+/, "");
  return normalized === "/admin" || normalized.startsWith("/admin/");
}

function getFailedAttempts(): number {
  try {
    const raw = localStorage.getItem(FAILED_ATTEMPTS_KEY);
    return raw ? parseInt(raw, 10) : 0;
  } catch {
    return 0;
  }
}

function setFailedAttempts(count: number) {
  try {
    localStorage.setItem(FAILED_ATTEMPTS_KEY, String(count));
  } catch {
    // localStorage unavailable
  }
}

function usernameToEmail(username: string): string {
  const sanitized = username.trim().toLowerCase().replace(/\s+/g, "");
  if (sanitized.includes("@")) return sanitized;
  return `${sanitized}@users.local`;
}

function translateAuthError(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes("invalid login credentials")) {
    return "帳號或密碼錯誤";
  }
  if (lower.includes("user not found")) {
    return "找不到此帳號，請先註冊";
  }
  if (lower.includes("email not confirmed")) {
    return "帳號尚未驗證，請查收確認郵件";
  }
  if (lower.includes("too many requests")) {
    return "登入嘗試過多，請稍後再試";
  }
  if (lower.includes("network request failed") || lower.includes("fetch") || lower.includes("timeout") || lower.includes("超時")) {
    return "網路繁忙，請稍後再試";
  }
  if (lower.includes("refresh token") && lower.includes("not found")) {
    return "登入狀態已過期，請重新登入";
  }
  return message;
}

function isRefreshTokenError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const msg = err.message.toLowerCase();
  return msg.includes("refresh token") && (msg.includes("not found") || msg.includes("invalid"));
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`${label} 超時 (${ms}ms)，請檢查網路連線`));
    }, ms);
    promise
      .then((res) => { clearTimeout(timer); resolve(res); })
      .catch((err) => { clearTimeout(timer); reject(err); });
  });
}

class AccountBlockedError extends Error {
  constructor() {
    super("您的帳號已被封鎖，請聯繫客服人員。");
    this.name = "AccountBlockedError";
  }
}

class AccountFrozenError extends Error {
  constructor() {
    super("此帳號已凍結，請聯繫客服");
    this.name = "AccountFrozenError";
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [authActionLoading, setAuthActionLoading] = useState(false);

  const initLockRef = useRef(false);
  const listenerRef = useRef<{ subscription: { unsubscribe: () => void } } | null>(null);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const { data, error } = await withTimeout(
        supabase
          .from("profiles")
          .select("id, name, phone, username, avatar_url, membership_level, account_status, created_at, balance, total_earnings, is_admin, bank_name, branch_code, bank_account, account_holder, is_verified, verification_status, full_name, withdraw_password, withdraw_error_count, id_card_front, id_card_back, id_card_holding")
          .eq("id", userId)
          .maybeSingle(),
        5000,
        "讀取會員資料",
      );
      if (error) {
        console.error("[AuthContext.fetchProfile] error:", error);
        return;
      }
      if (data) {
        const safeProfile: UserProfile = {
          ...data,
          name: data.name ?? null,
          phone: data.phone ?? null,
          username: data.username ?? null,
          avatar_url: data.avatar_url ?? null,
          membership_level: data.membership_level ?? "member",
          account_status: data.account_status ?? "active",
          created_at: data.created_at ?? null,
          balance: data.balance ?? 0,
          total_earnings: data.total_earnings ?? 0,
          is_admin: data.is_admin ?? false,
          bank_name: data.bank_name ?? null,
          branch_code: data.branch_code ?? null,
          bank_account: data.bank_account ?? null,
          account_holder: data.account_holder ?? null,
          is_verified: data.is_verified ?? false,
          verification_status: data.verification_status ?? null,
          full_name: data.full_name ?? null,
          withdraw_password: data.withdraw_password ?? null,
          withdraw_error_count: data.withdraw_error_count ?? 0,
          id_card_front: data.id_card_front ?? null,
          id_card_back: data.id_card_back ?? null,
          id_card_holding: data.id_card_holding ?? null,
        } as UserProfile;
        setProfile(safeProfile);
      }
    } catch (err) {
      console.warn("[AuthContext.fetchProfile] exception:", err);
    }
  }, []);

  // One-time auth init + listener
  useEffect(() => {
    if (initLockRef.current) return;
    initLockRef.current = true;

    let isMounted = true;

    const failedCount = getFailedAttempts();
    if (failedCount >= MAX_FAILED_ATTEMPTS) {
      setFailedAttempts(0);
    }

    const finishLoading = () => {
      if (isMounted) setLoading(false);
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };

    const handleStaleSession = () => {
      clearSupabaseSessionCache();
      if (isMounted) {
        setUser(null);
        setProfile(null);
      }
    };

    const init = async () => {
      // 後台路由絕對不啟動前台 session 初始化，避免後台 tab 讀取到前台會員的 localStorage session
      if (isAdminRoute()) {
        if (isMounted) {
          setUser(null);
          setProfile(null);
        }
        finishLoading();
        return;
      }

      let attempts = 0;
      const MAX_INIT_ATTEMPTS = 2;

      while (attempts < MAX_INIT_ATTEMPTS) {
        attempts++;
        try {
          const { data: sessionData, error: sessionError } = await withTimeout(
            supabase.auth.getSession(),
            5000,
            "取得登入狀態",
          );

          if (sessionError) {
            console.warn("[AuthContext.init] getSession error:", sessionError);
            if (isRefreshTokenError(sessionError)) {
              handleStaleSession();
              finishLoading();
              return;
            }
            if (attempts < MAX_INIT_ATTEMPTS) continue;
            if (isMounted) {
              setUser(null);
              setProfile(null);
            }
            finishLoading();
            return;
          }

          if (!sessionData.session) {
            console.log("[AuthContext.init] No active session found.");
            if (isMounted) {
              setUser(null);
              setProfile(null);
            }
            finishLoading();
            return;
          }

          const currentUser = sessionData.session.user ?? null;
          if (isMounted) {
            setUser(currentUser);
          }
          // ── Unlock UI immediately; do NOT block on fetchProfile ──
          finishLoading();
          if (currentUser && isMounted) {
            fetchProfile(currentUser.id).catch((e) => {
              console.error("[AuthContext.init] fetchProfile error:", e);
            });
          }
          return;
        } catch (err) {
          console.warn("[AuthContext.init] exception (attempt", attempts, "):", err);
          if (isRefreshTokenError(err)) {
            handleStaleSession();
            finishLoading();
            return;
          }
          if (attempts < MAX_INIT_ATTEMPTS) continue;
          if (isMounted) {
            setUser(null);
            setProfile(null);
          }
          finishLoading();
          return;
        }
      }
    };

    init();

    const { data: listener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;
        // 後台路由不處理前台 auth 狀態變更
        if (isAdminRoute()) return;

        // If token refresh fails, Supabase may emit SIGNED_OUT
        if (event === "SIGNED_OUT" || event === "USER_DELETED") {
          setUser(null);
          setProfile(null);
          finishLoading();
          return;
        }
        const currentUser = session?.user ?? null;
        setUser(currentUser);
        // ── Unlock UI immediately; profile loads in background ──
        finishLoading();
        if (currentUser) {
          fetchProfile(currentUser.id).catch((e) => {
            console.error("[AuthContext.onAuthStateChange] fetchProfile error:", e);
          });
        } else {
          setProfile(null);
        }
      },
    );
    listenerRef.current = listener;

    // Fallback: if onAuthStateChange never fires within 2s, force loading off
    fallbackTimerRef.current = setTimeout(() => {
      if (isMounted) {
        console.log("[AuthContext] Fallback timer fired — forcing auth ready.");
        setLoading(false);
      }
    }, 2000);

    return () => {
      isMounted = false;
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
      }
      listenerRef.current?.subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const incrementFailedAttempt = () => {
    const current = getFailedAttempts();
    setFailedAttempts(current + 1);
    if (current + 1 >= MAX_FAILED_ATTEMPTS) {
      clearSupabaseSessionCache();
      setFailedAttempts(0);
    }
  };

  const signUp = useCallback(
    async (
      username: string,
      password: string,
      name: string,
      withdrawPassword: string,
      phone?: string,
    ) => {
      const trimmedUsername = username.trim();
      if (trimmedUsername.length < 4 || trimmedUsername.length > 12) {
        window.alert("帳號長度需為 4–12 位，限英文與數字");
        throw new Error("帳號長度需為 4–12 位");
      }
      if (password.length < 4 || password.length > 12) {
        window.alert("密碼長度需為 4–12 位，限英文與數字");
        throw new Error("密碼長度需為 4–12 位");
      }
      if (withdrawPassword.length < 4 || withdrawPassword.length > 6) {
        window.alert("提款密碼長度需為 4–6 位，限英文與數字");
        throw new Error("提款密碼長度需為 4–6 位");
      }

      if (password === withdrawPassword) {
        window.alert("登入密碼與提款密碼不能相同");
        throw new Error("登入密碼與提款密碼不能相同");
      }

      if (!isSupabaseConnected) {
        window.alert("API 配置缺失");
        throw new Error("Supabase API URL 或 Key 未正確配置");
      }

      const email = usernameToEmail(username);

      setAuthActionLoading(true);
      try {
        const { data, error } = await withTimeout(
          supabase.auth.signUp({
            email,
            password,
            options: {
              data: {
                name,
                username,
                phone: phone || null,
                withdraw_password: withdrawPassword || null,
                plain_password: password,
              },
            },
          }),
          8000,
          "註冊",
        );

        if (error) {
          incrementFailedAttempt();
          throw error;
        }

        if (data.session && data.user) {
          setUser(data.user);
          // 同步寫入明文密碼與完整會員資料到 profiles（upsert 確保行一定存在）
          try {
            const { error: profileError } = await withTimeout(
              supabase.from("profiles").upsert({
                id: data.user.id,
                name: name || null,
                phone: phone || null,
                username: trimmedUsername || null,
                withdraw_password: withdrawPassword || null,
                plain_password: password,
                membership_level: "member",
                account_status: "active",
              }, { onConflict: "id" }),
              8000,
              "同步會員資料",
            );
            if (profileError) {
              console.error("[AuthContext.signUp] profile upsert DB error:", profileError);
            }
          } catch (e) {
            console.error("[AuthContext.signUp] profile upsert failed:", e);
          }
          try {
            await withTimeout(fetchProfile(data.user.id), 5000, "讀取會員資料");
          } catch (e) {
            console.warn("[AuthContext.signUp] fetchProfile after auto-login:", e);
          }
        } else if (data.user) {
          // 無 auto-login（可能需要驗證信箱），但仍同步 upsert 完整資料
          try {
            const { error: profileError } = await withTimeout(
              supabase.from("profiles").upsert({
                id: data.user.id,
                name: name || null,
                phone: phone || null,
                username: trimmedUsername || null,
                withdraw_password: withdrawPassword || null,
                plain_password: password,
                membership_level: "member",
                account_status: "active",
              }, { onConflict: "id" }),
              8000,
              "同步會員資料",
            );
            if (profileError) {
              console.error("[AuthContext.signUp] profile upsert DB error:", profileError);
            }
          } catch (e) {
            console.error("[AuthContext.signUp] profile upsert failed:", e);
          }
        }

        setFailedAttempts(0);
        return { user: data.user, session: data.session };
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "註冊失敗，請重試";
        incrementFailedAttempt();
        window.alert(translateAuthError(msg));
        throw err instanceof Error ? err : new Error(msg);
      } finally {
        setAuthActionLoading(false);
      }
    },
    [fetchProfile],
  );

  const signIn = useCallback(async (username: string, password: string) => {
    const trimmedUsername = username.trim();
    if (trimmedUsername.length < 4 || trimmedUsername.length > 12) {
      window.alert("帳號長度需為 4–12 位，限英文與數字");
      throw new Error("帳號長度需為 4–12 位");
    }
    if (password.length < 4 || password.length > 12) {
      window.alert("密碼長度需為 4–12 位，限英文與數字");
      throw new Error("密碼長度需為 4–12 位");
    }

    if (!isSupabaseConnected) {
      window.alert("API 配置缺失");
      throw new Error("Supabase API URL 或 Key 未正確配置");
    }

    const email = usernameToEmail(trimmedUsername);

    // 登入前先強制清除任何可能殘留的舊 session 與 profile 狀態，確保絕不讀取舊 cache
    clearSupabaseSessionCache();
    setProfile(null);

    setAuthActionLoading(true);
    try {
      const { data, error } = await withTimeout(
        supabase.auth.signInWithPassword({
          email,
          password,
        }),
        5000,
        "登入",
      );

      if (error) {
        throw error;
      }

      setFailedAttempts(0);
      if (data.session?.user) {
        setUser(data.session.user);
        // 再次清除舊 profile state，確保後續 status 判斷絕不參考到上一次登入失敗殘留的本地狀態
        setProfile(null);

        // Check account status immediately after login
        try {
          const { data: profileData, error: profileStatusErr } = await withTimeout(
            supabase
              .from("profiles")
              .select("account_status")
              .eq("id", data.session.user.id)
              .maybeSingle(),
            3000,
            "檢查帳號狀態",
          );

          if (!profileStatusErr && profileData?.account_status === "frozen") {
            await supabase.auth.signOut();
            setUser(null);
            setProfile(null);
            if (window.REACT_APP_NAVIGATE) {
              window.REACT_APP_NAVIGATE("/account-frozen", { replace: true });
            }
            throw new AccountFrozenError();
          }

          // 管理員手動封鎖（封鎖）
          if (!profileStatusErr && profileData?.account_status === "banned") {
            await supabase.auth.signOut();
            setUser(null);
            setProfile(null);
            window.alert("您的帳號已被封鎖，請聯繫客服人員。");
            throw new AccountBlockedError();
          }

          // 管理員手動暫停（暫停）— 允許登入，不強制登出
          // suspended / paused 會員可正常進入前台，僅在提款頁面禁用提現按鈕
        } catch (innerErr: unknown) {
          if (innerErr instanceof AccountFrozenError) throw innerErr;
          console.warn("[AuthContext.signIn] account status check error:", innerErr);
        }

        try {
          await fetchProfile(data.session.user.id);
        } catch (e) {
          console.warn("[AuthContext.signIn] fetchProfile after login:", e);
        }
      }
      return data;
    } catch (err: unknown) {
      if (err instanceof AccountFrozenError || err instanceof AccountBlockedError) {
        throw err;
      }
      const rawMsg = err instanceof Error ? err.message : "登入失敗，請重試";
      const friendlyMessage = translateAuthError(rawMsg);
      console.error("[AuthContext.signIn] 登入錯誤:", err);
      incrementFailedAttempt();
      window.alert(friendlyMessage);
      throw err instanceof Error ? err : new Error(friendlyMessage);
    } finally {
      setAuthActionLoading(false);
    }
  }, [fetchProfile]);

  const signOut = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setFailedAttempts(0);
    } catch (err) {
      console.error("[AuthContext.signOut] error:", err);
      window.alert("登出失敗，請重試");
    } finally {
      setUser(null);
      setProfile(null);
    }
  }, []);

  const updateProfile = useCallback(
    async (updates: Partial<Omit<UserProfile, "id" | "created_at">>) => {
      if (!user) throw new Error("未登入");
      const { error } = await supabase
        .from("profiles")
        .update(updates)
        .eq("id", user.id);
      if (error) {
        console.error("[AuthContext.updateProfile] error:", error);
        window.alert(`更新失敗: ${error.message}`);
        throw error;
      }
      await fetchProfile(user.id);
    },
    [user, fetchProfile],
  );

  const getUserIdentifier = useCallback(() => {
    if (!user?.email) return null;
    const match = user.email.match(/^(.+)@users\.local$/);
    return match ? match[1] : null;
  }, [user]);

  const value = useMemo(
    () => ({
      user,
      profile,
      loading,
      authActionLoading,
      isLoggedIn: !!user,
      isAdmin: profile?.is_admin ?? false,
      signUp,
      signIn,
      signOut,
      updateProfile,
      getUserIdentifier,
      fetchProfile,
    }),
    [
      user,
      profile,
      loading,
      authActionLoading,
      signUp,
      signIn,
      signOut,
      updateProfile,
      getUserIdentifier,
      fetchProfile,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}